import React, { useState, useEffect } from 'react';
import { Upload, Trash2, LogOut, Save, Image as ImageIcon, Plus, Edit2, X } from 'lucide-react';
import { auth, db, storage } from '../firebase';
import { signInWithEmailAndPassword, signOut, onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, setDoc, collection, query, where, getDocs, addDoc, deleteDoc, orderBy, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { useDentoData } from '../context/DentoContext';
import { Smile } from 'lucide-react';

export const AdminDashboard = () => {
    const [user, setUser] = useState(null);
    const [activeTab, setActiveTab] = useState('general');
    const { hospitalData, updateHospitalData, doctors: initialDoctors, slides: initialSlides } = useDentoData();

    // Auth & Form states
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [formData, setFormData] = useState({});
    const [loading, setLoading] = useState(false);

    // Dynamic Lists State
    const [images, setImages] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('hero');
    const [doctorList, setDoctorList] = useState([]);
    const [slideList, setSlideList] = useState([]);

    // Modals
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editItem, setEditItem] = useState(null); // Item being edited
    const [modalMode, setModalMode] = useState(''); // 'doctor' or 'slide'
    const [imagePreview, setImagePreview] = useState(null); // Preview for uploaded image
    const [uploadingImage, setUploadingImage] = useState(false);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, u => setUser(u));
        return () => unsubscribe();
    }, []);

    useEffect(() => {
        if (hospitalData) setFormData(hospitalData);
    }, [hospitalData]);

    useEffect(() => {
        // Refresh local lists when tab changes
        if (activeTab === 'images') fetchImages();
        if (activeTab === 'doctors') fetchDoctors();
        if (activeTab === 'hero') fetchSlides();
    }, [activeTab, selectedCategory]);

    const fetchImages = async () => {
        const q = query(collection(db, 'images'), where('category', '==', selectedCategory));
        const snap = await getDocs(q);
        const imagesList = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        // Sort client-side to avoid index requirement
        imagesList.sort((a, b) => (b.uploadedAt || '').localeCompare(a.uploadedAt || ''));
        setImages(imagesList);
    };

    const fetchDoctors = async () => {
        // We can fetch from context or firestore. Fetching from firestore ensures fresh data for admin.
        const snap = await getDocs(collection(db, 'doctors'));
        const doctorsList = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        // Sort client-side to avoid index requirement
        doctorsList.sort((a, b) => (a.order || 0) - (b.order || 0));
        setDoctorList(doctorsList);
    };

    const fetchSlides = async () => {
        const snap = await getDocs(collection(db, 'slides'));
        const slidesList = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        // Sort client-side to avoid index requirement
        slidesList.sort((a, b) => (a.order || 0) - (b.order || 0));
        setSlideList(slidesList);
    };

    // --- Helper Functions ---

    // Convert image file to Base64 string (as documented in IMAGE_UPLOAD_ARCHITECTURE.md)
    const convertToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = (error) => reject(error);
        });
    };

    // --- Handlers ---


    const handleLogin = async (e) => {
        e.preventDefault();
        try { await signInWithEmailAndPassword(auth, email, password); }
        catch (e) { alert(e.message); }
    };

    const handleSaveGeneral = async () => {
        setLoading(true);
        await updateHospitalData(formData);
        alert("Saved!");
        setLoading(false);
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Firestore Document Limit is 1MB. We limit to 700KB to be safe.
        if (file.size > 700 * 1024) {
            alert("File too large! Please choose an image under 700KB for the Free Database.");
            return;
        }

        setLoading(true);

        try {
            // Convert to Base64 using helper function
            const base64Url = await convertToBase64(file);

            // Save DIRECTLY to Firestore (Bypassing Storage Bucket)
            await addDoc(collection(db, 'images'), {
                url: base64Url, // The image data itself
                category: selectedCategory,
                uploadedAt: new Date().toISOString(),
                name: file.name,
                storageType: 'base64' // Flag to know it's not a storage URL
            });

            alert("Image Uploaded Successfully (DB)!");
            fetchImages();
        } catch (error) {
            console.error("Upload error:", error);
            alert("Upload Failed: " + error.message);
        }

        setLoading(false);
    };

    const handleDeleteImage = async (image) => {
        if (!window.confirm("Delete this image?")) return;
        try {
            // Only delete from Firestore (since we aren't using Storage bucket anymore)
            await deleteDoc(doc(db, 'images', image.id));
            // If it WAS a storage image (old one), we might try to delete it, but ignore error
            if (image.path && !image.storageType) {
                try {
                    const storageRef = ref(storage, image.path);
                    await deleteObject(storageRef);
                } catch (e) { console.log("Storage delete skipped", e); }
            }

            fetchImages();
        } catch (error) {
            alert("Delete Failed: " + error.message);
        }
    };

    // Handle image upload in Doctor/Slide Modal
    const handleModalImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Firestore Document Limit is 1MB. We limit to 700KB to be safe.
        if (file.size > 700 * 1024) {
            alert("File too large! Please choose an image under 700KB.");
            return;
        }

        setUploadingImage(true);

        try {
            // Convert to Base64 using helper function
            const base64Url = await convertToBase64(file);
            setImagePreview(base64Url);
            setEditItem({ ...editItem, image: base64Url });
        } catch (error) {
            console.error("Image conversion error:", error);
            alert("Failed to process image: " + error.message);
        }

        setUploadingImage(false);
    };

    // Generic Add/Edit Handler for Doctors/Slides
    const handleSaveItem = async (e) => {
        e.preventDefault();
        setLoading(true);
        const collectionName = modalMode === 'doctor' ? 'doctors' : 'slides';
        const itemData = { ...editItem };

        // Remove ID from data if present
        const id = itemData.id;
        delete itemData.id;

        try {
            // Upload Image if present in file input (we handle this separately usually, 
            // but here let's assume valid URL is pasted OR we add file upload to form)
            // For simplicity, I'll assume URL is entered or we add a quick upload button in modal.

            if (id) {
                await updateDoc(doc(db, collectionName, id), itemData);
            } else {
                itemData.order = Date.now(); // Simple ordering
                await addDoc(collection(db, collectionName), itemData);
            }
            setIsModalOpen(false);
            if (modalMode === 'doctor') fetchDoctors();
            else fetchSlides();
        } catch (e) { alert(e.message); }
        setLoading(false);
    };

    const handleDelete = async (collectionName, id) => {
        if (!window.confirm("Delete?")) return;
        await deleteDoc(doc(db, collectionName, id));
        if (collectionName === 'doctors') fetchDoctors();
        else fetchSlides();
    };

    // Render Logic
    if (!user) return <LoginScreen email={email} setEmail={setEmail} password={password} setPassword={setPassword} handleLogin={handleLogin} />;

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col">
            {/* Header */}
            <div className="bg-white shadow px-6 py-4 flex justify-between items-center z-10">
                <h1 className="text-xl font-bold flex-1 flex items-center gap-2">
                    <Smile className="h-8 w-8 text-[var(--dento-blue)]" /> Dento Care Admin Panel
                </h1>
                <div className="flex items-center gap-4">
                    <span className="text-sm text-gray-600">{user.email}</span>
                    <button onClick={() => signOut(auth)} className="text-red-500 flex gap-1 items-center"><LogOut size={16} /> Logout</button>
                </div>
            </div>

            <div className="flex flex-1 container mx-auto p-6 gap-6">
                {/* Sidebar */}
                <div className="w-64 flex-shrink-0">
                    <div className="bg-white rounded-lg shadow overflow-hidden">
                        {[
                            { id: 'general', label: 'General Settings', icon: '⚙️' },
                            { id: 'hero', label: 'Hero Slider', icon: '🖼️' },
                            { id: 'doctors', label: 'Doctors', icon: '👨‍⚕️' },
                            { id: 'images', label: 'Image Assets', icon: '📂' },
                        ].map(tab => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`w-full text-left px-4 py-3 flex items-center gap-2 border-b last:border-0 hover:bg-gray-50 ${activeTab === tab.id ? 'bg-blue-50 text-blue-600 font-medium' : 'text-gray-700'}`}
                            >
                                <span>{tab.icon}</span> {tab.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Main Content */}
                <div className="flex-1 bg-white rounded-lg shadow p-6">
                    {activeTab === 'general' && (
                        <GeneralSettingsForm formData={formData} setFormData={setFormData} onSave={handleSaveGeneral} loading={loading} />
                    )}

                    {activeTab === 'images' && (
                        <ImageManager
                            images={images}
                            loading={loading}
                            selectedCategory={selectedCategory}
                            setSelectedCategory={setSelectedCategory}
                            onUpload={handleImageUpload}
                            onDelete={(id) => handleDelete('images', id)}
                        />
                    )}

                    {(activeTab === 'doctors' || activeTab === 'hero') && (
                        <div className="h-full flex flex-col">
                            <div className="flex justify-between items-center mb-6">
                                <h2 className="text-xl font-bold capitalize">{activeTab === 'hero' ? 'Hero Slides' : 'Doctors List'}</h2>
                                <button
                                    onClick={() => {
                                        setModalMode(activeTab === 'hero' ? 'slide' : 'doctor');
                                        setEditItem({});
                                        setImagePreview(null);
                                        setIsModalOpen(true);
                                    }}
                                    className="bg-blue-600 text-white px-4 py-2 rounded flex items-center gap-2 hover:bg-blue-700"
                                >
                                    <Plus size={18} /> Add New
                                </button>
                            </div>

                            <div className="grid gap-4">
                                {(activeTab === 'hero' ? slideList : doctorList).map(item => (
                                    <div key={item.id} className="border rounded p-4 flex gap-4 items-center">
                                        <img src={item.image} alt="preview" className="w-20 h-20 object-cover rounded bg-gray-100" />
                                        <div className="flex-1">
                                            <h3 className="font-bold">{item.title || item.name}</h3>
                                            <p className="text-sm text-gray-500">{item.subtitle || item.speciality}</p>
                                        </div>
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => {
                                                    setModalMode(activeTab === 'hero' ? 'slide' : 'doctor');
                                                    setEditItem(item);
                                                    setImagePreview(item.image || null);
                                                    setIsModalOpen(true);
                                                }}
                                                className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                                            >
                                                <Edit2 size={18} />
                                            </button>
                                            <button
                                                onClick={() => handleDelete(activeTab === 'hero' ? 'slides' : 'doctors', item.id)}
                                                className="p-2 text-red-600 hover:bg-red-50 rounded"
                                            >
                                                <Trash2 size={18} />
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-bold capitalize">{editItem?.id ? 'Edit' : 'Add'} {modalMode}</h3>
                            <button onClick={() => setIsModalOpen(false)}><X className="w-6 h-6 text-gray-500" /></button>
                        </div>
                        <form onSubmit={handleSaveItem} className="space-y-4">
                            {modalMode === 'doctor' ? (
                                <>
                                    <input required placeholder="Doctor Name" value={editItem.name || ''} onChange={e => setEditItem({ ...editItem, name: e.target.value })} className="w-full p-2 border rounded" />
                                    <input required placeholder="Speciality" value={editItem.speciality || ''} onChange={e => setEditItem({ ...editItem, speciality: e.target.value })} className="w-full p-2 border rounded" />
                                    <input placeholder="Qualification" value={editItem.qualification || ''} onChange={e => setEditItem({ ...editItem, qualification: e.target.value })} className="w-full p-2 border rounded" />
                                    <input placeholder="Experience" value={editItem.experience || ''} onChange={e => setEditItem({ ...editItem, experience: e.target.value })} className="w-full p-2 border rounded" />
                                </>
                            ) : (
                                <>
                                    <input required placeholder="Title" value={editItem.title || ''} onChange={e => setEditItem({ ...editItem, title: e.target.value })} className="w-full p-2 border rounded" />
                                    <input placeholder="Subtitle" value={editItem.subtitle || ''} onChange={e => setEditItem({ ...editItem, subtitle: e.target.value })} className="w-full p-2 border rounded" />
                                    <input placeholder="Button Text (CTA)" value={editItem.cta || ''} onChange={e => setEditItem({ ...editItem, cta: e.target.value })} className="w-full p-2 border rounded" />
                                </>
                            )}

                            <div>
                                <label className="block text-sm font-medium mb-2">Image</label>

                                {/* Image Preview */}
                                {imagePreview && (
                                    <div className="mb-3 relative">
                                        <img
                                            src={imagePreview}
                                            alt="Preview"
                                            className="w-full h-48 object-cover rounded border"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setImagePreview(null);
                                                setEditItem({ ...editItem, image: '' });
                                            }}
                                            className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full hover:bg-red-600"
                                        >
                                            <X size={16} />
                                        </button>
                                    </div>
                                )}

                                {/* Upload Button */}
                                <label className="block">
                                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition">
                                        <Upload className="mx-auto mb-2 text-gray-400" size={32} />
                                        <p className="text-sm text-gray-600">
                                            {uploadingImage ? 'Uploading...' : 'Click to upload image'}
                                        </p>
                                        <p className="text-xs text-gray-400 mt-1">Max 700KB</p>
                                    </div>
                                    <input
                                        type="file"
                                        className="hidden"
                                        onChange={handleModalImageUpload}
                                        accept="image/*"
                                        disabled={uploadingImage}
                                    />
                                </label>

                                {/* Optional: Manual URL Entry */}
                                <div className="mt-3">
                                    <details className="text-xs">
                                        <summary className="cursor-pointer text-gray-500 hover:text-gray-700">Or paste image URL</summary>
                                        <input
                                            type="text"
                                            placeholder="https://..."
                                            value={editItem.image || ''}
                                            onChange={e => {
                                                setEditItem({ ...editItem, image: e.target.value });
                                                setImagePreview(e.target.value);
                                            }}
                                            className="w-full p-2 border rounded mt-2 text-sm"
                                        />
                                    </details>
                                </div>
                            </div>

                            <div className="flex justify-end gap-2 mt-6">
                                <button type="button" onClick={() => setIsModalOpen(false)} className="px-4 py-2 border rounded">Cancel</button>
                                <button type="submit" disabled={loading} className="px-4 py-2 bg-blue-600 text-white rounded">{loading ? 'Saving...' : 'Save'}</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

// Sub-components for cleaner code
const LoginScreen = ({ email, setEmail, password, setPassword, handleLogin }) => (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="bg-white p-8 rounded-xl shadow-lg w-full max-w-md">
            <div className="flex justify-center mb-6">
                <div className="w-20 h-20 rounded-2xl overflow-hidden shadow-xl border border-gray-100">
                    <img src="/logo/dentocare1.png" alt="Dento Care Logo" className="w-full h-full object-cover" />
                </div>
            </div>
            <h1 className="text-2xl font-bold text-center mb-6">Dento Care Admin Login</h1>
            <form onSubmit={handleLogin} className="space-y-4">
                <input type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full p-2 border rounded" />
                <input type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full p-2 border rounded" />
                <button type="submit" className="w-full bg-blue-600 text-white py-2 rounded">Sign In</button>
            </form>
        </div>
    </div>
);

const GeneralSettingsForm = ({ formData, setFormData, onSave, loading }) => (
    <div>
        <h2 className="text-xl font-bold mb-4">General Settings</h2>
        <div className="space-y-4 max-w-lg">
            {['name', 'emergency', 'email', 'phone', 'calLink'].map(field => (
                <div key={field}>
                    <label className="block text-sm font-medium mb-1 capitalize">
                        {field === 'calLink' ? 'Cal.com Link (e.g. username/event)' : field}
                    </label>
                    <input name={field} value={formData[field] || ''} onChange={e => setFormData({ ...formData, [field]: e.target.value })} className="w-full p-2 border rounded" />
                </div>
            ))}
            <div>
                <label className="block text-sm font-medium mb-1">Address</label>
                <textarea name="address" value={formData.address || ''} onChange={e => setFormData({ ...formData, address: e.target.value })} className="w-full p-2 border rounded h-24" />
            </div>
            <button onClick={onSave} disabled={loading} className="bg-green-600 text-white px-6 py-2 rounded flex items-center gap-2 hover:bg-green-700">
                <Save size={18} /> {loading ? 'Saving...' : 'Save Changes'}
            </button>
        </div>
    </div>
);

const ImageManager = ({ images, loading, selectedCategory, setSelectedCategory, onUpload, onDelete }) => (
    <div>
        <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold">Image Assets</h2>
            <label className="bg-blue-600 text-white px-4 py-2 rounded cursor-pointer hover:bg-blue-700 flex items-center gap-2">
                <Upload size={18} /> {loading ? 'Uploading...' : 'Upload New'}
                <input type="file" className="hidden" onChange={onUpload} accept="image/*" disabled={loading} />
            </label>
        </div>
        <div className="flex space-x-2 mb-6 overflow-x-auto pb-2">
            {['hero', 'doctors', 'gallery', 'services'].map(cat => (
                <button key={cat} onClick={() => setSelectedCategory(cat)} className={`px-4 py-1 rounded-full text-sm capitalize whitespace-nowrap ${selectedCategory === cat ? 'bg-gray-800 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}>
                    {cat}
                </button>
            ))}
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {images.map(img => (
                <div key={img.id} className="group relative border rounded overflow-hidden aspect-square bg-gray-100">
                    <img src={img.url} alt="asset" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2">
                        <button onClick={() => { navigator.clipboard.writeText(img.url); alert("URL Copied!"); }} className="bg-white text-gray-900 px-3 py-1 rounded text-xs font-bold hover:bg-gray-200">Copy URL</button>
                        <button onClick={() => onDelete(img.id)} className="bg-red-500 text-white p-2 rounded-full hover:bg-red-600"><Trash2 size={16} /></button>
                    </div>
                </div>
            ))}
        </div>
    </div>
);
